// Utility functions for making authenticated API calls

import { API_CONFIG } from './config';
import { authService } from '../services/authService';

/**
 * Makes an authenticated fetch request with automatic token validation and 401 error handling
 * @param url - The API endpoint URL (relative or absolute)
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns Promise<Response>
 * @throws Error if token is missing, expired, or if request fails with 401
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Get and validate token
  const token = authService.getValidToken();

  // Ensure URL is absolute
  const absoluteUrl = url.startsWith('http') ? url : `${API_CONFIG.backendUrl}${url}`;

  // Merge headers, ensuring Authorization header is set
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  
  // Set Content-Type if not already set and body is provided
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Make the request
  const response = await fetch(absoluteUrl, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized (token expired or invalid)
  if (response.status === 401) {
    authService.removeToken();
    throw new Error('Session expired. Please log in again.');
  }

  return response;
}

